import { nanoid } from "nanoid";
import { type ReactNode, useEffect } from "react";
import { type Id, toast, type ToastOptions } from "react-toastify";

import { Notification, type NotificationProps } from "./Notification.tsx";
import { NotificationIntent } from "./NotificationIntent.tsx";

export interface NotifyToast {
  default: (props: NotificationProps, intent: NotificationIntent, options?: NotificationOptions) => Id;
  info: (props: NotificationProps, options?: NotificationOptions) => Id;
  success: (props: NotificationProps, options?: NotificationOptions) => Id;
  warning: (props: NotificationProps, options?: NotificationOptions) => Id;
  error: (props: NotificationProps, options?: NotificationOptions) => Id;
  withJSX: (element: ReactNode, toastId?: string | number, options?: NotificationOptions) => Id;
  update: (
    currentToastId: string | number,
    props: NotificationProps,
    intent: NotificationIntent,
    options?: NotificationOptions,
  ) => void;
  clearWaitingQueue: () => void;
}

export interface NotificationOptions
  extends Pick<ToastOptions, "toastId" | "autoClose" | "containerId" | "delay" | "pauseOnFocusLoss"> {
  markAsReadOverInteraction?: boolean;
}

export const notify: NotifyToast = {
  default: (props: NotificationProps, intent: NotificationIntent, options: NotificationOptions = {}) => {
    const toastId: Id = options.toastId ?? nanoid();
    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn("Create %s notification in container '%s'", intent, options.containerId);
      console.warn(
        "Passed id was '%s', and the ID used is '%s' (in case passed id is null/undefined this is a random string or number)",
        options.toastId,
        toastId,
      );
    }

    return toast(<Notification {...props} currentToastId={toastId} intent={intent} />, {
      ...options,
      toastId,
    });
  },
  info: (props: NotificationProps, options: NotificationOptions = {}) =>
    notify.default(props, NotificationIntent.Info, options),
  success: (props: NotificationProps, options: NotificationOptions = {}) =>
    notify.default(props, NotificationIntent.Success, options),
  warning: (props: NotificationProps, options: NotificationOptions = {}) =>
    notify.default(props, NotificationIntent.Warning, options),
  error: (props: NotificationProps, options: NotificationOptions = {}) =>
    notify.default(props, NotificationIntent.Error, options),
  withJSX(element: ReactNode, toastId?: string | number, options: NotificationOptions = {}) {
    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn("Create JSX notification in container '%s'", options.containerId);
      console.warn(
        "Passed id was '%s', and the ID used is '%s' (in case passed id is null/undefined this is a random string or number)",
        options.toastId,
        toastId,
      );
    }
    return toast(element, { ...options, toastId });
  },
  update: (
    currentToastId: string | number,
    props: NotificationProps,
    intent: NotificationIntent,
    options: NotificationOptions = {},
  ) => {
    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn(
        "Updating notification with title '%s' to intent '%s', currentToastId: '%s', idFromProps: '%s'",
        props.title,
        intent,
        currentToastId,
        props.currentToastId,
      );
    }
    if (toast.isActive(currentToastId)) {
      toast.update(currentToastId, {
        render: () => <Notification {...props} currentToastId={currentToastId} intent={intent} />,
        autoClose: 5000,
        ...options,
      });
    } else {
      notify.default(props, intent, options);
    }
  },
  clearWaitingQueue: toast.clearWaitingQueue,
};

export const notificationDismiss = (toastId: string | number) => {
  if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
    console.warn("Dismiss notification with id '%s'", toastId);
  }
  toast.dismiss(toastId);
};

export const useNotificationRemoved = (toastId: string | number | undefined, callback: () => void) => {
  if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
    console.warn("use notification removed hook called with id '%s'", toastId);
  }
  useEffect(() => {
    return toast.onChange((payload) => {
      if (payload.id === toastId && payload.status === "removed") {
        callback();
      }
    });
  }, [callback, toastId]);
};
